import "@components/ag-grid/styles/AgCharts.css";
import {
  AgBarSeriesOptions,
  AgCartesianChartOptions,
  AgLineSeriesOptions,
} from "ag-charts-community";
import { AgCharts } from "ag-charts-react";
import React, { useMemo } from "react";

type Props = {
  data: any[];
  title: string;
  revenueKey: string;
  volumeKey: string;
  dataType?: "Monthly" | "Daily";
};

const DualAxisChart: React.FC<Props> = ({
  data,
  title,
  revenueKey,
  volumeKey,
  dataType = "Monthly",
}) => {
  const getEstValue = (
    actual: number,
    dateValue: string,
    type: "Monthly" | "Daily"
  ) => {
    if (type === "Daily" || actual === 0) return null;

    const monthDate = new Date(dateValue + "-01");
    const daysInMonth = new Date(
      monthDate.getFullYear(),
      monthDate.getMonth() + 1,
      0
    ).getDate();

    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === monthDate.getFullYear() &&
      today.getMonth() === monthDate.getMonth();

    if (!isCurrentMonth) return null;

    const daysCompleted = today.getDate();
    return Math.round((actual * daysInMonth) / daysCompleted);
  };

  const formatAxisDate = (dateString: string, type: "Monthly" | "Daily") => {
    if (type === "Monthly") {
      return dateString;
    } else {
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        return `${day}-${month}`;
      } catch {
        return dateString;
      }
    }
  };

  const chartData = useMemo(
    () =>
      data
        .map((item) => {
          const revenue = Number(item[revenueKey]) || 0;
          const volume = Number(item[volumeKey]) || 0;
          const dateField = dataType === "Monthly" ? item.Month : item.Day;

          return {
            month: dateField,
            revenue,
            volume,
            revenueEst: getEstValue(revenue, dateField, dataType),
            volumeEst: getEstValue(volume, dateField, dataType),
          };
        })
        .reverse(),
    [data, revenueKey, volumeKey, dataType]
  );

  const chartOptions: AgCartesianChartOptions = useMemo(() => {
    const series: AgCartesianChartOptions["series"] = [];

    // --- Actual Volume Line ---
    const volumeLine: AgLineSeriesOptions = {
      type: "line",
      xKey: "month",
      yKey: "volume",
      yName: "Volume",
      stroke: "#EE6666",
      strokeWidth: 2,
      marker: { size: 4, fill: "#EE6666" },
      interpolation: { type: "smooth" },
    };
    series.push(volumeLine);

    // --- Estimated Volume Line (NO SPREAD) ---
    if (dataType === "Monthly") {
      const volumeEstLine: AgLineSeriesOptions = {
        type: "line",
        xKey: "month",
        yKey: "volumeEst",
        yName: "Volume (Est.)",
        stroke: "#EE6666",
        strokeWidth: 2,
        lineDash: [6, 4],
        marker: {
          enabled: true,
          size: 8,
          fill: "#EE6666",
        },
      };
      series.push(volumeEstLine);
    }

    // --- Revenue Bar ---
    const revenueBar: AgBarSeriesOptions = {
      type: "bar",
      xKey: "month",
      yKey: "revenue",
      yName: "Revenue",
      fill: "#5470C6",
    };
    series.push(revenueBar);

    // --- Estimated Revenue Line (dots only) ---
    if (dataType === "Monthly") {
      const revenueEstLine: AgLineSeriesOptions = {
        type: "line",
        xKey: "month",
        yKey: "revenueEst",
        yName: "Revenue (Est.)",
        stroke: "#5470C6",
        strokeWidth: 0,
        marker: {
          enabled: true,
          size: 6,
          shape: "square",
          fill: "#5470C6",
          stroke: "#5470C6",
          strokeWidth: 1,
        },
      };
      series.push(revenueEstLine);
    }

    return {
      title: {
        text: title,
        fontSize: 10,
        fontWeight: 600,
        color: "var(--text-base)",
      },

      data: chartData,
      series,

      axes: [
        {
          type: "category",
          position: "bottom",
          label: {
            rotation: 45,
            fontSize: 9,
            color: "var(--text-base)",
            formatter: (params) => formatAxisDate(params.value, dataType),
          },
        },
        {
          type: "number",
          position: "left",
          keys: dataType === "Monthly" ? ["volume", "volumeEst"] : ["volume"],
          label: {
            fontSize: 9,
            color: "var(--text-base)",
            formatter: (p) => p.value.toLocaleString(),
          },
        },
        {
          type: "number",
          position: "right",
          keys:
            dataType === "Monthly" ? ["revenue", "revenueEst"] : ["revenue"],
          label: {
            fontSize: 9,
            color: "var(--text-base)",
            formatter: (p) => `$${p.value.toLocaleString()}`,
          },
        },
      ],

      legend: {
        enabled: true,
        position: "bottom",
        item: { label: { fontSize: 10 } },
      },

      background: { fill: "transparent" },
      padding: { top: 0, bottom: 40, left: 0, right: 0 },
    };
  }, [chartData, title, dataType]);

  return (
    <div
      style={{ width: "100%", height: "35vh" }}
      className="border border-border-subtle rounded-lg bg-bg-base p-2 w-[300px]"
    >
      <AgCharts options={chartOptions} />
    </div>
  );
};

export default DualAxisChart;
